"use client";

import { c as _c } from "react/compiler-runtime";
import { arc as arcGenerator } from "@visx/shape";
import { motion, useSpring, useTransform } from "motion/react";
import { useEffect, useRef } from "react";
import { usePie } from "./pie-context";

// Helper to generate arc path using d3 arc generator
function generateArcPath(innerRadius: number, outerRadius: number, startAngle: number, endAngle: number, cornerRadius: number, padAngle: number): string {
  const generator = arcGenerator<unknown>({
    innerRadius,
    outerRadius,
    cornerRadius,
    padAngle
  });
  return generator({
    startAngle,
    endAngle
  } as unknown as null) || "";
}

// Calculate the translation offset for a slice to "pop out" along its radial axis
function getSliceOffset(startAngle: number, endAngle: number, distance: number): {
  x: number;
  y: number;
} {
  // Calculate the midpoint angle of the slice
  const midAngle = (startAngle + endAngle) / 2;
  // In d3-shape, 0 radians is at 12 o'clock, angles increase clockwise
  // So the outward direction is: x = sin(angle), y = -cos(angle)
  return {
    x: Math.sin(midAngle) * distance,
    y: -Math.cos(midAngle) * distance
  };
}

/** Hover effect types */
export type PieSliceHoverEffect = "translate" | "grow" | "none";
export interface PieSliceProps {
  /** Index of the slice in the data array */
  index: number;
  /** Optional color override - falls back to data color or palette */
  color?: string;
  /** Optional fill override for patterns/gradients (e.g., "url(#patternId)") */
  fill?: string;
  /** Animate the slice on mount. Default: true */
  animate?: boolean;
  /** Show glow effect on hover. Default: true */
  showGlow?: boolean;
  /**
   * Hover effect type. Default: "translate"
   * - "translate": Slice moves outward along its radial axis
   * - "grow": Slice extends its outer radius (gets longer)
   * - "none": No hover animation
   */
  hoverEffect?: PieSliceHoverEffect;
  /** Distance in pixels for hover effect (translate distance or grow amount). Defaults to PieChart's hoverOffset */
  hoverOffset?: number;
  /** Additional CSS class */
  className?: string;
}
interface AnimatedSliceTranslateProps {
  index: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  cornerRadius: number;
  padAngle: number;
  fill: string;
  color: string;
  isHovered: boolean;
  isFaded: boolean;
  animationKey: number;
  showGlow: boolean;
  hoverOffset: number;
}
function AnimatedSliceTranslate(t0) {
  const $ = _c(29);
  const {
    index,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    cornerRadius,
    padAngle,
    fill,
    color,
    isHovered,
    isFaded,
    animationKey,
    showGlow,
    hoverOffset
  } = t0;
  const animationDelay = 0.1 + index * 0.08;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 60,
      damping: 20,
      restDelta: 0.001
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mountSpring = useSpring(0, t1);
  let t2;
  let t3;
  if ($[1] !== animationDelay || $[2] !== mountSpring) {
    t2 = () => {
      mountSpring.jump(0);
      const timeout = setTimeout(() => {
        mountSpring.set(1);
      }, animationDelay * 1000);
      return () => clearTimeout(timeout);
    };
    t3 = [animationDelay, mountSpring];
    $[1] = animationDelay;
    $[2] = mountSpring;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== cornerRadius || $[6] !== endAngle || $[7] !== innerRadius || $[8] !== outerRadius || $[9] !== padAngle || $[10] !== startAngle) {
    t4 = mount => {
      const currentEndAngle = startAngle + (endAngle - startAngle) * mount;
      if (currentEndAngle <= startAngle + 0.01) {
        return "";
      }
      return generateArcPath(innerRadius, outerRadius, startAngle, currentEndAngle, cornerRadius, padAngle);
    };
    $[5] = cornerRadius;
    $[6] = endAngle;
    $[7] = innerRadius;
    $[8] = outerRadius;
    $[9] = padAngle;
    $[10] = startAngle;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const animatedPath = useTransform(mountSpring, t4);
  let t5;
  if ($[12] !== endAngle || $[13] !== hoverOffset || $[14] !== startAngle) {
    t5 = getSliceOffset(startAngle, endAngle, hoverOffset);
    $[12] = endAngle;
    $[13] = hoverOffset;
    $[14] = startAngle;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const offset = t5;
  const glowColor = color;
  const t6 = isFaded ? 0.4 : 1;
  const t7 = isHovered ? offset.x : 0;
  const t8 = isHovered ? offset.y : 0;
  let t9;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = {
      opacity: t6,
      x: t7,
      y: t8
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `slice-${animationKey}-${index}`;
  const t11 = showGlow && isHovered ? `drop-shadow(0 0 12px ${glowColor})` : "none";
  let t12;
  if ($[20] !== t11) {
    t12 = {
      filter: t11
    };
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      opacity: {
        duration: 0.15
      },
      x: {
        type: "spring",
        stiffness: 400,
        damping: 25
      },
      y: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== animatedPath || $[24] !== fill || $[25] !== t10 || $[26] !== t12 || $[27] !== t9) {
    t14 = <motion.path animate={t9} d={animatedPath} fill={fill} key={t10} pointerEvents="none" style={t12} transition={t13} />;
    $[23] = animatedPath;
    $[24] = fill;
    $[25] = t10;
    $[26] = t12;
    $[27] = t9;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  return t14;
}
interface AnimatedSliceGrowProps {
  index: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  cornerRadius: number;
  padAngle: number;
  fill: string;
  color: string;
  isHovered: boolean;
  isFaded: boolean;
  animationKey: number;
  showGlow: boolean;
  hoverOffset: number;
}
function AnimatedSliceGrow(t0) {
  const $ = _c(32);
  const {
    index,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    cornerRadius,
    padAngle,
    fill,
    color,
    isHovered,
    isFaded,
    animationKey,
    showGlow,
    hoverOffset
  } = t0;
  const animationDelay = 0.1 + index * 0.08;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 60,
      damping: 20,
      restDelta: 0.001
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const mountSpring = useSpring(0, t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 400,
      damping: 25
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const growSpring = useSpring(outerRadius, t2);
  let t3;
  let t4;
  if ($[2] !== animationDelay || $[3] !== mountSpring) {
    t3 = () => {
      mountSpring.jump(0);
      const timeout = setTimeout(() => {
        mountSpring.set(1);
      }, animationDelay * 1000);
      return () => clearTimeout(timeout);
    };
    t4 = [animationDelay, mountSpring];
    $[2] = animationDelay;
    $[3] = mountSpring;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[6] !== growSpring || $[7] !== hoverOffset || $[8] !== isHovered || $[9] !== outerRadius) {
    t5 = () => {
      growSpring.set(isHovered ? outerRadius + hoverOffset : outerRadius);
    };
    t6 = [isHovered, hoverOffset, outerRadius, growSpring];
    $[6] = growSpring;
    $[7] = hoverOffset;
    $[8] = isHovered;
    $[9] = outerRadius;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== growSpring || $[13] !== mountSpring) {
    t7 = [mountSpring, growSpring];
    $[12] = growSpring;
    $[13] = mountSpring;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== cornerRadius || $[16] !== endAngle || $[17] !== innerRadius || $[18] !== padAngle || $[19] !== startAngle) {
    t8 = t9 => {
      const [mount, currentOuterRadius] = t9;
      const currentEndAngle = startAngle + (endAngle - startAngle) * (mount as number);
      if (currentEndAngle <= startAngle + 0.01) {
        return "";
      }
      return generateArcPath(innerRadius, currentOuterRadius as number, startAngle, currentEndAngle, cornerRadius, padAngle);
    };
    $[15] = cornerRadius;
    $[16] = endAngle;
    $[17] = innerRadius;
    $[18] = padAngle;
    $[19] = startAngle;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const animatedPath = useTransform(t7, t8);
  const glowColor = color;
  const t9 = isFaded ? 0.4 : 1;
  let t10;
  if ($[21] !== t9) {
    t10 = {
      opacity: t9
    };
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = `slice-${animationKey}-${index}`;
  const t12 = showGlow && isHovered ? `drop-shadow(0 0 12px ${glowColor})` : "none";
  let t13;
  if ($[23] !== t12) {
    t13 = {
      filter: t12
    };
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      opacity: {
        duration: 0.15
      }
    };
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== animatedPath || $[27] !== fill || $[28] !== t10 || $[29] !== t11 || $[30] !== t13) {
    t15 = <motion.path animate={t10} d={animatedPath} fill={fill} key={t11} pointerEvents="none" style={t13} transition={t14} />;
    $[26] = animatedPath;
    $[27] = fill;
    $[28] = t10;
    $[29] = t11;
    $[30] = t13;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}
export function PieSlice(t0) {
  const $ = _c(73);
  const {
    index,
    color: colorProp,
    fill: fillProp,
    animate: t1,
    showGlow: t2,
    hoverEffect: t3,
    hoverOffset: hoverOffsetProp
  } = t0;
  const animate = t1 === undefined ? true : t1;
  const showGlow = t2 === undefined ? true : t2;
  const hoverEffect = t3 === undefined ? "translate" : t3;
  const {
    arcs,
    innerRadius,
    outerRadius,
    cornerRadius,
    hoverOffset: contextHoverOffset,
    hoveredIndex,
    setHoveredIndex,
    animationKey,
    getColor,
    getFill
  } = usePie();
  const hoverOffset = hoverOffsetProp ?? contextHoverOffset;
  const hasAnimated = useRef(false);
  const sliceExpandDelay = index * 0.08;
  let t4;
  let t5;
  if ($[0] !== animate || $[1] !== sliceExpandDelay) {
    t4 = () => {
      if (animate && !hasAnimated.current) {
        const timeout = setTimeout(() => {
          hasAnimated.current = true;
        }, (sliceExpandDelay + 0.5) * 1000);
        return () => clearTimeout(timeout);
      }
    };
    t5 = [animate, sliceExpandDelay];
    $[0] = animate;
    $[1] = sliceExpandDelay;
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  const arcData = arcs[index];
  if (!arcData) {
    return null;
  }
  let t6;
  if ($[4] !== colorProp || $[5] !== getColor || $[6] !== index) {
    t6 = colorProp || getColor(index);
    $[4] = colorProp;
    $[5] = getColor;
    $[6] = index;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const color = t6;
  let t7;
  if ($[8] !== fillProp || $[9] !== getFill || $[10] !== index) {
    t7 = fillProp || getFill(index);
    $[8] = fillProp;
    $[9] = getFill;
    $[10] = index;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const fill = t7;
  const isHovered = hoveredIndex === index;
  const isFaded = hoveredIndex !== null && hoveredIndex !== index;
  let t8;
  if ($[12] !== arcData.endAngle || $[13] !== arcData.startAngle || $[14] !== hoverOffset) {
    t8 = getSliceOffset(arcData.startAngle, arcData.endAngle, hoverOffset);
    $[12] = arcData.endAngle;
    $[13] = arcData.startAngle;
    $[14] = hoverOffset;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const offset = t8;
  let t9;
  if ($[16] !== arcData.endAngle || $[17] !== arcData.padAngle || $[18] !== arcData.startAngle || $[19] !== cornerRadius || $[20] !== innerRadius || $[21] !== outerRadius) {
    t9 = generateArcPath(innerRadius, outerRadius, arcData.startAngle, arcData.endAngle, cornerRadius, arcData.padAngle);
    $[16] = arcData.endAngle;
    $[17] = arcData.padAngle;
    $[18] = arcData.startAngle;
    $[19] = cornerRadius;
    $[20] = innerRadius;
    $[21] = outerRadius;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const hitboxPath = t9;
  const grownOuterRadius = isHovered ? outerRadius + hoverOffset : outerRadius;
  let t10;
  if ($[23] !== arcData.endAngle || $[24] !== arcData.padAngle || $[25] !== arcData.startAngle || $[26] !== cornerRadius || $[27] !== grownOuterRadius || $[28] !== innerRadius) {
    t10 = generateArcPath(innerRadius, grownOuterRadius, arcData.startAngle, arcData.endAngle, cornerRadius, arcData.padAngle);
    $[23] = arcData.endAngle;
    $[24] = arcData.padAngle;
    $[25] = arcData.startAngle;
    $[26] = cornerRadius;
    $[27] = grownOuterRadius;
    $[28] = innerRadius;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const grownPath = t10;
  let t11;
  if ($[30] !== animationKey || $[31] !== arcData.endAngle || $[32] !== arcData.padAngle || $[33] !== arcData.startAngle || $[34] !== color || $[35] !== cornerRadius || $[36] !== fill || $[37] !== hoverEffect || $[38] !== hoverOffset || $[39] !== index || $[40] !== innerRadius || $[41] !== isFaded || $[42] !== isHovered || $[43] !== outerRadius || $[44] !== showGlow) {
    t11 = () => {
      if (hoverEffect === "grow") {
        return <AnimatedSliceGrow animationKey={animationKey} color={color} cornerRadius={cornerRadius} endAngle={arcData.endAngle} fill={fill} hoverOffset={hoverOffset} index={index} innerRadius={innerRadius} isFaded={isFaded} isHovered={isHovered} outerRadius={outerRadius} padAngle={arcData.padAngle} showGlow={showGlow} startAngle={arcData.startAngle} />;
      }
      return <AnimatedSliceTranslate animationKey={animationKey} color={color} cornerRadius={cornerRadius} endAngle={arcData.endAngle} fill={fill} hoverOffset={hoverEffect === "none" ? 0 : hoverOffset} index={index} innerRadius={innerRadius} isFaded={isFaded} isHovered={isHovered} outerRadius={outerRadius} padAngle={arcData.padAngle} showGlow={showGlow} startAngle={arcData.startAngle} />;
    };
    $[30] = animationKey;
    $[31] = arcData.endAngle;
    $[32] = arcData.padAngle;
    $[33] = arcData.startAngle;
    $[34] = color;
    $[35] = cornerRadius;
    $[36] = fill;
    $[37] = hoverEffect;
    $[38] = hoverOffset;
    $[39] = index;
    $[40] = innerRadius;
    $[41] = isFaded;
    $[42] = isHovered;
    $[43] = outerRadius;
    $[44] = showGlow;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  const renderAnimatedSlice = t11;
  let t12;
  if ($[46] !== color || $[47] !== fill || $[48] !== grownPath || $[49] !== hitboxPath || $[50] !== hoverEffect || $[51] !== isFaded || $[52] !== isHovered || $[53] !== offset || $[54] !== showGlow) {
    t12 = () => {
      if (hoverEffect === "grow") {
        return <motion.path animate={{
          opacity: isFaded ? 0.4 : 1,
          d: grownPath
        }} d={hitboxPath} fill={fill} pointerEvents="none" style={{
          filter: showGlow && isHovered ? `drop-shadow(0 0 12px ${color})` : "none"
        }} transition={{
          opacity: {
            duration: 0.15
          },
          d: {
            type: "spring",
            stiffness: 400,
            damping: 25
          }
        }} />;
      }
      const shouldTranslate = hoverEffect !== "none" && isHovered;
      const translateX = shouldTranslate ? offset.x : 0;
      const translateY = shouldTranslate ? offset.y : 0;
      return <motion.path animate={{
        opacity: isFaded ? 0.4 : 1,
        x: translateX,
        y: translateY
      }} d={hitboxPath} fill={fill} pointerEvents="none" style={{
        filter: showGlow && isHovered ? `drop-shadow(0 0 12px ${color})` : "none"
      }} transition={{
        opacity: {
          duration: 0.15
        },
        x: {
          type: "spring",
          stiffness: 400,
          damping: 25
        },
        y: {
          type: "spring",
          stiffness: 400,
          damping: 25
        }
      }} />;
    };
    $[46] = color;
    $[47] = fill;
    $[48] = grownPath;
    $[49] = hitboxPath;
    $[50] = hoverEffect;
    $[51] = isFaded;
    $[52] = isHovered;
    $[53] = offset;
    $[54] = showGlow;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  const renderStaticSlice = t12;
  let t13;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      cursor: "pointer"
    };
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  let t14;
  if ($[57] !== index || $[58] !== setHoveredIndex) {
    t14 = () => setHoveredIndex(index);
    $[57] = index;
    $[58] = setHoveredIndex;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== setHoveredIndex) {
    t15 = () => setHoveredIndex(null);
    $[60] = setHoveredIndex;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== hitboxPath || $[63] !== t14 || $[64] !== t15) {
    t16 = <path d={hitboxPath} fill="transparent" onMouseEnter={t14} onMouseLeave={t15} />;
    $[62] = hitboxPath;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  let t17;
  if ($[66] !== animate || $[67] !== renderAnimatedSlice || $[68] !== renderStaticSlice) {
    t17 = animate ? renderAnimatedSlice() : renderStaticSlice();
    $[66] = animate;
    $[67] = renderAnimatedSlice;
    $[68] = renderStaticSlice;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== t16 || $[71] !== t17) {
    t18 = <g style={t13}>{t16}{t17}</g>;
    $[70] = t16;
    $[71] = t17;
    $[72] = t18;
  } else {
    t18 = $[72];
  }
  return t18;
}
PieSlice.displayName = "PieSlice";
export default PieSlice;